import React, { ReactNode, useCallback, useEffect } from "react";

import Drawer, { DrawerProps } from "antd/es/drawer";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { useIntl } from "react-intl";

import useRerenderer from "@mapmycustomers/shared/util/hook/useRerenderer";

import consts from "@app/component/preview/styles/_consts.module.scss";
import ResizeHandle from "@app/component/ResizeHandle";
import localSettings from "@app/config/LocalSettings";
import useMapSidebar from "@app/scene/map/utils/useMapSidebar";
import { DEFAULT_SIDEBAR_WIDTH } from "@app/util/ui/getDefaultSidebarWidth";
import getPageContentContainer from "@app/util/ui/getPageContentContainer";

import styles from "./Sidebar.module.scss";

interface Props extends DrawerProps {
  beforeTitle?: ReactNode;
  children?: React.ReactNode;
  className?: string;
  id?: string;
  maxWidth?: number;
  menu?: ReactNode;
  minWidth?: number;
  onClose: () => void;
  onResized?: (size: number) => void;
  title?: ReactNode;
  titleRaw?: ReactNode;
  visible?: boolean;
  widthKey?: string;
  wrapper?: boolean;
}

const Sidebar: React.FC<Props> = ({
  id,
  beforeTitle,
  children,
  className,
  maxWidth = parseInt(consts.maxPaneWidth, 10),
  menu,
  minWidth = DEFAULT_SIDEBAR_WIDTH,
  onClose,
  onResized,
  title,
  titleRaw,
  visible,
  widthKey,
  wrapper,
  ...props
}) => {
  const intl = useIntl();
  const forceRender = useRerenderer();

  const setSidebarWidth = useMapSidebar(id);

  // extracted as a separate var because I'm thinking about making it dynamic in a future
  const width = localSettings.getRecordPaneWidth(widthKey);

  useEffect(() => {
    setSidebarWidth(visible ? width : 0);
  }, [width, setSidebarWidth, visible]);

  // close sidebar when it is unmounted
  // Do not merge this and the above useEffects because we only wanna set width to 0
  // when component is unmounted, not every time width changes
  useEffect(() => () => setSidebarWidth(0), [setSidebarWidth]);

  const handleResize = useCallback(
    (width) => {
      if (visible) {
        setSidebarWidth(width);
        localSettings.setRecordPaneWidth(width, widthKey, minWidth, maxWidth);
        onResized?.(width);
        forceRender();
      }
    },
    [forceRender, maxWidth, minWidth, onResized, setSidebarWidth, widthKey, visible]
  );

  const drawerTitle =
    title ??
    intl.formatMessage({
      id: "map.recordList.title.default",
      defaultMessage: "Records in view",
      description: "Records List default title",
    });

  return visible ? (
    <Drawer
      className={cn(styles.container, { [styles.containerWrapper]: wrapper }, className)}
      closable={!wrapper && !!onClose}
      contentWrapperStyle={{ maxWidth: `calc(min(99vw, ${maxWidth}px)` }}
      getContainer={getPageContentContainer()}
      mask={false}
      onClose={onClose}
      placement="right"
      push={{ distance: 0 }}
      title={
        wrapper
          ? null
          : titleRaw ?? (
              <>
                {beforeTitle}
                <div className={styles.titleContainer}>
                  <span className={styles.title}>
                    <Tooltip title={drawerTitle}>{drawerTitle}</Tooltip>
                  </span>
                  <div>{menu}</div>
                </div>
              </>
            )
      }
      visible
      width={width}
      {...props}
    >
      <ResizeHandle
        container={getPageContentContainer()}
        max={maxWidth}
        maxPercent={66}
        min={minWidth}
        onResize={handleResize}
      />
      {children}
    </Drawer>
  ) : null;
};

export default Sidebar;
